import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../Icon';
import PhoneWhiteIcon from '../../../public/static/images/phone_call_white-icon.svg';
import ContactInfoModal from '../../ContactInfoModal';
import ContactsList from '../../ContactInfoModal/ContactsList';
import PhoneIcon from '../../../public/static/images/item/round-phone.svg';
import CallPhoneIcon from '../../../public/static/images/phone_call_brown-icon.svg';
import ContactsItem from '../../ContactInfoModal/ContactsItem';
import {
  formatAzerbaijanPhone,
  phoneWithInternationalCode
} from '../../../utils/helpers/common';
import useScreenSize from '../../../hooks/useScreenSize';
import { BottomPhoneButtonProps } from './types';
import {
  Column,
  MaskedPhoneText,
  PhoneButtonContainer,
  PhoneButtonText,
  PhoneLink,
  PhoneLinksContainer,
  Row
} from './styled';
import { PhoneButtonTargetTypeEnum } from '../../../hooks/useFullPhoneNumbers';
import { StickyPhoneButton } from '../../BottomPhoneButton/StickyPhoneButton';

export const BottomPhoneButton: React.FC<BottomPhoneButtonProps> = memo(
  ({
    stat,
    phones = [],
    clickHandler,
    isIconButton = false,
    isAuthor = false,
    isGallery = false,
    setIsVisible,
    isVisible,
    contactInfoModalTitle
  }) => {
    const { t } = useTranslation();
    const { isSmall, isLarge, isSmallDesktop } = useScreenSize();

    const onClickHandler = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();

      if (!isAuthor) {
        clickHandler(0, false)(
          e,
          isGallery
            ? PhoneButtonTargetTypeEnum.gallery
            : PhoneButtonTargetTypeEnum.main
        );
      }
    };

    const isSmallOrLargeDesktop = isLarge || isSmallDesktop;
    const isOnePhone = phones?.length === 1;
    const firstPhone = phones?.[0];
    const phonesToRender = isGallery ? firstPhone && [firstPhone] : phones;

    const stickyButton = (
      <StickyPhoneButton
        isOnePhone={isOnePhone}
        stat={stat}
        phones={phones}
        isButtonSticky={!isSmallOrLargeDesktop}
        onClick={onClickHandler}
        isCurrentItemPage
      />
    );

    const iconButton = (
      <i
        role="button"
        tabIndex={-1}
        onKeyPress={onClickHandler}
        onClick={onClickHandler}
        data-stat={stat}
        aria-label={t('common.call')}
      >
        <Icon width={50} height={50} IconComponent={PhoneIcon} indent={false} />
      </i>
    );

    const phoneIcon = (isGallery || (!isGallery && !phones)) && (
      <Icon
        IconComponent={PhoneWhiteIcon}
        width={20}
        height={20}
        indent={false}
      />
    );

    const phoneButtonText = !isSmall && !phones?.length && (
      <PhoneButtonText tag="body_1" $isGallery={isGallery}>
        {t('pages.current_item.buttons.show_phone')}
      </PhoneButtonText>
    );

    const onPhoneLinkClick = (phone: string) =>
      window.open(`tel:${phoneWithInternationalCode(phone)}`, '_top');

    return (
      <>
        {!isGallery &&
          (isIconButton ? iconButton : !isSmallOrLargeDesktop && stickyButton)}

        {(isGallery || isSmallOrLargeDesktop) && (
          <PhoneButtonContainer
            onClick={onClickHandler}
            $isPhoneShown={!!firstPhone && !isSmall}
            $isGallery={isGallery}
          >
            {(!phones?.length || (phones.length && isSmall)) &&
              (isGallery ? (
                <Row $isGallery>
                  {phoneIcon}
                  {phoneButtonText}
                </Row>
              ) : (
                <Column>
                  {phoneButtonText}
                  <Row $isGallery={false}>
                    {phoneIcon}

                    {!isGallery && !phones?.length && (
                      <MaskedPhoneText tag="h2">+994 ••• </MaskedPhoneText>
                    )}
                  </Row>
                </Column>
              ))}

            <PhoneLinksContainer>
              {phonesToRender &&
                !isSmall &&
                phonesToRender.map(phone => (
                  <PhoneLink
                    key={phone}
                    href={`tel:${phoneWithInternationalCode(phone)}`}
                    $isGallery={isGallery}
                    onClick={() => onPhoneLinkClick(phone)}
                  >
                    {formatAzerbaijanPhone(phoneWithInternationalCode(phone))}
                  </PhoneLink>
                ))}
            </PhoneLinksContainer>
          </PhoneButtonContainer>
        )}

        {!isOnePhone && isVisible && !isGallery && !isSmallOrLargeDesktop && (
          <ContactInfoModal
            isOpen={isVisible}
            closeHandler={() => setIsVisible?.(false)}
            title={contactInfoModalTitle ?? t('common.call')}
          >
            {phones?.length ? (
              phones?.map((phone, i) => (
                <ContactsItem
                  icon={
                    <Icon
                      width={17}
                      height={17}
                      IconComponent={CallPhoneIcon}
                      indent={false}
                    />
                  }
                  text={phone}
                  key={`phone-number-${i}`}
                  path={`tel:${phoneWithInternationalCode(phone)}`}
                  index={0}
                />
              ))
            ) : (
              <ContactsList
                showPhone
                isCurrentItemPage
                currentItemPhones={phones}
              />
            )}
          </ContactInfoModal>
        )}
      </>
    );
  }
);

BottomPhoneButton.displayName = 'BottomPhoneButton';
